// Ops · Shops (docs/OPS-PEOPLE-CONTRACT.md "Back office"): invite-only onboarding with a vetting
// record. The add/edit form edits strings; validateShopDraft() turns them into the wire shape
// (OpsShopCreate) with the contract's limits, and shopPatch() sends only what changed.
// Pure: no DOM, no Intl. node --test runs it.

import { shopSlugProblem, suggestShopSlug, tidyShopSlug } from '@popcart/core/v1';
/** @typedef {import('@popcart/core/v1').OpsShop} OpsShopDetail */
/** @typedef {import('@popcart/core/v1').OpsShopCreate} OpsShopCreate */
/** @typedef {import('@popcart/core/v1').ProductLine} ProductLine */
/** @typedef {import('@popcart/core/v1').LatLng} LatLng */

export const shopKeys = {
  /** @param {string} id */
  one: (id) => ['ops', 'shop', id],
};

/** @type {{ value: ProductLine; label: string }[]} */
export const LINE_OPTIONS = [
  { value: 'fresh', label: 'Fresh' },
  { value: 'grill', label: 'Grill' },
  { value: 'mart', label: 'Grocery' },
];

export const NAME_MIN = 2;
export const NAME_MAX = 80;
export const REG_MAX = 40;
export const DEFAULT_BPS = 1500;
export const MAX_BPS = 5000;
export const PREP_MIN = 5;
export const PREP_MAX = 120;
export const DEFAULT_PREP = 20;
export const MIN_ORDER_MAX_CENTAVOS = 500000;
export const TIN_RE = /^\d{3}-?\d{3}-?\d{3}(-?\d{3,5})?$/;
export { SLUG_MAX } from '@popcart/core/v1';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** A generous box around the Philippines (the API checks the real outline). */
export const PH_BOUNDS = { south: 4.2, north: 21.3, west: 116.0, east: 127.0 };

/** @param {LatLng | null | undefined} p */
export function inPhilippines(p) {
  if (!p || !Number.isFinite(p.lat) || !Number.isFinite(p.lng)) return false;
  return p.lat >= PH_BOUNDS.south && p.lat <= PH_BOUNDS.north && p.lng >= PH_BOUNDS.west && p.lng <= PH_BOUNDS.east;
}

/** Round to ~1 cm. @param {LatLng} p @returns {LatLng} */
export function roundLatLng(p) {
  return { lat: Math.round(p.lat * 1e7) / 1e7, lng: Math.round(p.lng * 1e7) / 1e7 };
}

/** "14.65070, 121.03000". @param {LatLng | null | undefined} p */
export function latLngLabel(p) {
  if (!p || !Number.isFinite(p.lat) || !Number.isFinite(p.lng)) return 'No pin yet';
  return `${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}`;
}

// ── commission: percent on screen, basis points on the wire ────────────────────────────────

/** 1500 → "15", 1250 → "12.5", 1234 → "12.34". @param {number | null | undefined} bps */
export function bpsToPercentInput(bps) {
  if (typeof bps !== 'number' || !Number.isFinite(bps)) return '';
  return String(Math.round(bps) / 100);
}

/** "15" → 1500, "12.5" → 1250; null when it isn't a percent with at most 2 decimals. @param {string} s */
export function percentInputToBps(s) {
  const t = String(s ?? '').trim().replace(/%$/, '').trim();
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(Number(t) * 100);
}

/** "15%" / "12.5%". @param {number | null | undefined} bps */
export function bpsLabel(bps) {
  if (typeof bps !== 'number' || !Number.isFinite(bps)) return '—';
  return `${Math.round(bps) / 100}%`;
}

// ── money ───────────────────────────────────────────────────────────────────────────────────

/** 15000 → "150", 4950 → "49.50". @param {number | null | undefined} c */
export function centavosToInput(c) {
  if (typeof c !== 'number' || !Number.isFinite(c)) return '';
  const whole = Math.trunc(c / 100);
  const cents = Math.abs(c % 100);
  return cents ? `${whole}.${String(cents).padStart(2, '0')}` : String(whole);
}

/** "150" → 15000, "49.5" → 4950; null when it isn't pesos and centavos. @param {string} s */
export function pesoInputToCentavos(s) {
  const t = String(s ?? '').trim().replace(/^₱/, '').replace(/,/g, '').trim();
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(Number(t) * 100);
}

// ── contact ─────────────────────────────────────────────────────────────────────────────────

/** Digits (and a leading +) only. @param {string} s */
export function phoneDigits(s) {
  const t = String(s ?? '').trim();
  return (t.startsWith('+') ? '+' : '') + t.replace(/\D/g, '');
}

/** A PH mobile (09XX… / +639XX…) or landline (7–10 digits, optional 0 or +63). @param {string} s */
export function isPhPhone(s) {
  const d = phoneDigits(s);
  return /^(?:\+63|0)9\d{9}$/.test(d) || /^(?:\+63|0)?\d{7,10}$/.test(d);
}

/** Trimmed, lower-case. @param {unknown} s */
export function normalizeEmail(s) {
  return String(s ?? '').trim().toLowerCase();
}

/** @param {string} s */
export function emailOk(s) {
  const e = normalizeEmail(s);
  return e.length <= 254 && EMAIL_RE.test(e);
}

/** The short name a shop name suggests (the API makes the real, unique one). @param {string} name */
export function slugPreview(name) {
  return String(name ?? '').trim() ? suggestShopSlug(name) : '';
}

/** The public host hub addresses live on (docs/SHOP-SHORT-URLS.md). */
export const HUB_HOST = 'hub.popcartph.com';

// ── the form ────────────────────────────────────────────────────────────────────────────────

/**
 * @typedef {{
 *   name: string; kind: 'first_party' | 'partner'; lines: ProductLine[];
 *   addressText: string; location: LatLng | null; phone: string; contactEmail: string;
 *   commissionPct: string; prepMinutes: string; minOrderPesos: string;
 *   legalName: string; registrationNo: string; tin: string; opsNotes: string; ownerEmail: string;
 *   slug: string;
 * }} ShopDraft
 */

/** @typedef {'name' | 'kind' | 'lines' | 'addressText' | 'location' | 'phone' | 'contactEmail' | 'commissionPct' | 'prepMinutes' | 'minOrderPesos' | 'legalName' | 'registrationNo' | 'tin' | 'opsNotes' | 'ownerEmail' | 'slug'} DraftField */

/** A blank Add shop form. @param {number} [defaultBps] settings.marketplace.defaultCommissionBps @returns {ShopDraft} */
export function emptyShopDraft(defaultBps = DEFAULT_BPS) {
  return {
    name: '',
    kind: 'partner',
    lines: [],
    addressText: '',
    location: null,
    phone: '',
    contactEmail: '',
    commissionPct: bpsToPercentInput(defaultBps),
    prepMinutes: String(DEFAULT_PREP),
    minOrderPesos: '0',
    legalName: '',
    registrationNo: '',
    tin: '',
    opsNotes: '',
    ownerEmail: '',
    slug: '',
  };
}

/** The edit form, from the server's shop. @param {OpsShopDetail} s @returns {ShopDraft} */
export function draftFromShop(s) {
  return {
    name: s.name ?? '',
    kind: s.kind,
    lines: [...(s.lines ?? [])],
    addressText: s.addressText ?? '',
    location: s.location ?? null,
    phone: s.phone ?? '',
    contactEmail: s.contactEmail ?? '',
    commissionPct: bpsToPercentInput(s.commissionBps),
    prepMinutes: String(s.prepMinutes ?? DEFAULT_PREP),
    minOrderPesos: centavosToInput(s.minOrderCentavos ?? 0),
    legalName: s.legalName ?? '',
    registrationNo: s.registrationNo ?? '',
    tin: s.tin ?? '',
    opsNotes: s.opsNotes ?? '',
    ownerEmail: '',
    slug: s.slug ?? '',
  };
}

/** @param {string} s */
const blankToNull = (s) => {
  const t = String(s ?? '').trim();
  return t ? t : null;
};

/**
 * Validate the form. Returns the wire body (OpsShopCreate; `ownerEmail` only when creating) or
 * field errors in plain words.
 * @param {ShopDraft} d
 * @param {{ mode?: 'create' | 'edit' }} [opts]
 * @returns {{ ok: true; value: OpsShopCreate } | { ok: false; errors: Partial<Record<DraftField, string>> }}
 */
export function validateShopDraft(d, opts = {}) {
  const mode = opts.mode ?? 'create';
  /** @type {Partial<Record<DraftField, string>>} */
  const errors = {};
  const name = d.name.trim().replace(/\s+/g, ' ');
  if (name.length < NAME_MIN) errors.name = 'Give the shop a name (2 letters or more).';
  else if (name.length > NAME_MAX) errors.name = `${NAME_MAX} characters at most.`;
  if (d.kind !== 'partner' && d.kind !== 'first_party') errors.kind = 'Pick partner or first-party.';
  const lines = LINE_OPTIONS.map((o) => o.value).filter((l) => d.lines.includes(l));
  if (!lines.length) errors.lines = 'Pick at least one thing it sells.';
  const addressText = d.addressText.trim();
  if (addressText.length < 5) errors.addressText = 'Enter the pickup address riders go to.';
  else if (addressText.length > 200) errors.addressText = '200 characters at most.';
  if (!d.location) errors.location = 'Drop a pin where riders pick up.';
  else if (!inPhilippines(d.location)) errors.location = 'That pin is outside the Philippines. Move it to the shop.';

  const phone = d.phone.trim();
  if (phone && !isPhPhone(phone)) errors.phone = 'Use a PH mobile (0917 123 4567) or landline number.';
  const contactEmail = normalizeEmail(d.contactEmail);
  if (contactEmail && !emailOk(contactEmail)) errors.contactEmail = 'That email doesn’t look right.';

  const bps = percentInputToBps(d.commissionPct);
  if (bps === null) errors.commissionPct = 'A percent like 15 or 12.5.';
  else if (bps > MAX_BPS) errors.commissionPct = 'Between 0% and 50%.';

  const prepRaw = d.prepMinutes.trim();
  const prep = /^\d+$/.test(prepRaw) ? Number(prepRaw) : NaN;
  if (!Number.isInteger(prep)) errors.prepMinutes = 'Whole minutes, like 20.';
  else if (prep < PREP_MIN || prep > PREP_MAX) errors.prepMinutes = `Between ${PREP_MIN} and ${PREP_MAX} min.`;

  const minOrder = pesoInputToCentavos(d.minOrderPesos === '' ? '0' : d.minOrderPesos);
  if (minOrder === null) errors.minOrderPesos = 'Pesos and centavos only, like 150 or 49.50.';
  else if (minOrder > MIN_ORDER_MAX_CENTAVOS) errors.minOrderPesos = 'Between ₱0 and ₱5,000.';

  const legalName = blankToNull(d.legalName);
  if (legalName && legalName.length > 160) errors.legalName = '160 characters at most.';
  const registrationNo = blankToNull(d.registrationNo);
  if (registrationNo && registrationNo.length > REG_MAX) errors.registrationNo = `${REG_MAX} characters at most.`;
  const tin = blankToNull(d.tin);
  if (tin && !TIN_RE.test(tin)) errors.tin = 'A TIN looks like 123-456-789 or 123-456-789-00000.';
  const opsNotes = blankToNull(d.opsNotes);
  if (opsNotes && opsNotes.length > 2000) errors.opsNotes = '2,000 characters at most.';

  const ownerEmail = mode === 'create' ? normalizeEmail(d.ownerEmail) : '';
  if (ownerEmail && !emailOk(ownerEmail)) errors.ownerEmail = 'That email doesn’t look right.';

  // Web address: optional when adding (the API makes one from the name), required once it exists.
  const slug = tidyShopSlug(d.slug ?? '');
  if (slug || mode === 'edit') {
    const why = shopSlugProblem(slug);
    if (why) errors.slug = why;
  }

  if (Object.keys(errors).length) return { ok: false, errors };
  /** @type {OpsShopCreate} */
  const value = {
    name,
    kind: d.kind,
    lines,
    addressText,
    location: roundLatLng(/** @type {LatLng} */ (d.location)),
    phone: phone ? phoneDigits(phone) : null,
    contactEmail: contactEmail || null,
    commissionBps: /** @type {number} */ (bps),
    prepMinutes: prep,
    minOrderCentavos: /** @type {number} */ (minOrder),
    legalName,
    registrationNo,
    tin,
    opsNotes,
  };
  if (mode === 'create' && ownerEmail) value.ownerEmail = ownerEmail;
  if (slug) value.slug = slug;
  return { ok: true, value };
}

/**
 * Only the fields that changed from the server's shop (PATCH body). Empty object = nothing to save.
 * @param {OpsShopCreate} next validated form value
 * @param {OpsShopDetail} shop
 * @returns {Partial<Omit<OpsShopCreate, 'ownerEmail'>>}
 */
export function shopPatch(next, shop) {
  /** @type {Record<string, unknown>} */
  const out = {};
  const same = (/** @type {unknown} */ a, /** @type {unknown} */ b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  /** @type {(keyof Omit<OpsShopCreate, 'ownerEmail'>)[]} */
  const keys = ['name', 'kind', 'lines', 'addressText', 'location', 'phone', 'contactEmail', 'commissionBps', 'prepMinutes', 'minOrderCentavos', 'legalName', 'registrationNo', 'tin', 'opsNotes', 'slug'];
  for (const k of keys) {
    const was = k === 'location' && shop.location ? roundLatLng(shop.location) : /** @type {Record<string, unknown>} */ (/** @type {unknown} */ (shop))[k];
    if (!same(next[k], was)) out[k] = next[k];
  }
  return /** @type {Partial<Omit<OpsShopCreate, 'ownerEmail'>>} */ (out);
}

// ── verification and going live ─────────────────────────────────────────────────────────────

/**
 * What's still missing before Ops can verify (the API needs all four).
 * @param {{ legalName?: string | null; registrationNo?: string | null; tin?: string | null; location?: LatLng | null }} s
 * @returns {{ ready: boolean; missing: string[] }}
 */
export function verifyReadiness(s) {
  /** @type {string[]} */
  const missing = [];
  if (!String(s.legalName ?? '').trim()) missing.push('legal name');
  if (!String(s.registrationNo ?? '').trim()) missing.push('DTI/SEC registration no.');
  if (!String(s.tin ?? '').trim()) missing.push('TIN');
  if (!s.location) missing.push('map pin');
  return { ready: missing.length === 0, missing };
}

/** "legal name, TIN and map pin". @param {string[]} xs */
export function listWords(xs) {
  if (xs.length <= 1) return xs.join('');
  return `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
}

/** The sentence under a disabled Verify button, or null when it can be pressed. @param {Parameters<typeof verifyReadiness>[0]} s */
export function verifyHint(s) {
  const r = verifyReadiness(s);
  if (r.ready) return null;
  const words = listWords(r.missing);
  return `Add the ${words} and save, then verify.`;
}

/**
 * Does this shop still need checking? Partner shops only (first-party is PopCart's own). The
 * detail record has `verifiedAt`; a list row from an older API without it counts while pending.
 * @param {{ kind: string; status?: string; verifiedAt?: string | null }} s
 */
export function needsVerification(s) {
  if (s.kind !== 'partner') return false;
  if ('verifiedAt' in s) return !s.verifiedAt;
  return s.status === 'pending';
}

export const UNVERIFIED_LIVE = 'An unverified partner can’t go live. Check its papers, fill in the vetting record and verify it first.';

/** Why this shop can't go live (null = it can). @param {{ kind: string; status?: string; verifiedAt?: string | null }} s */
export function liveBlock(s) {
  return needsVerification(s) ? UNVERIFIED_LIVE : null;
}

/** @type {Record<string, { label: string; tone: 'success' | 'warning' | 'error' | 'neutral' }>} */
const STATUS = {
  pending: { label: 'Not live yet', tone: 'warning' },
  live: { label: 'Live', tone: 'success' },
  paused: { label: 'Paused', tone: 'warning' },
  suspended: { label: 'Suspended', tone: 'error' },
};
/** @param {string} status */
export function statusView(status) {
  return STATUS[status] ?? { label: status, tone: 'neutral' };
}

/**
 * The status buttons this shop offers, with why "Go live" is off when it is.
 * @param {{ kind: string; status: string; verifiedAt?: string | null }} s
 * @returns {{ status: 'live' | 'paused' | 'suspended'; label: string; disabled: boolean; reason: string | null }[]}
 */
export function statusActions(s) {
  const block = liveBlock(s);
  /** @type {{ status: 'live' | 'paused' | 'suspended'; label: string; disabled: boolean; reason: string | null }[]} */
  const out = [];
  if (s.status !== 'live') out.push({ status: 'live', label: s.status === 'pending' ? 'Go live' : s.status === 'paused' ? 'Resume' : 'Reinstate', disabled: !!block, reason: block });
  if (s.status === 'live') out.push({ status: 'paused', label: 'Pause', disabled: false, reason: null });
  if (s.status !== 'suspended') out.push({ status: 'suspended', label: 'Suspend', disabled: false, reason: null });
  return out;
}

/**
 * Server field names → form field names, so an API 400 can sit under the right input.
 * @param {string | undefined} field
 * @returns {DraftField | null}
 */
export function formFieldFor(field) {
  if (!field) return null;
  /** @type {Record<string, DraftField>} */
  const map = { commissionBps: 'commissionPct', minOrderCentavos: 'minOrderPesos', 'location.lat': 'location', 'location.lng': 'location' };
  const known = ['name', 'kind', 'lines', 'addressText', 'location', 'phone', 'contactEmail', 'prepMinutes', 'legalName', 'registrationNo', 'tin', 'opsNotes', 'ownerEmail'];
  if (map[field]) return map[field];
  return known.includes(field) ? /** @type {DraftField} */ (field) : null;
}
